import { useState } from 'react';
import { useForgeNavigate } from '../components/layout/ForgeTransition';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { ChevronDown, ChevronUp, Play } from 'lucide-react';

interface ModelSection {
  title: string;
  bullets: string[];
}

const tclContent: ModelSection[] = [
  {
    title: 'The Model',
    bullets: [
      'Trend Continuation Ladder — pullback entries into established trends',
      'Trades only when the trend is confirmed: 20/50/200 EMAs stacked in direction',
      'High-frequency, small-winner system designed for prop firm consistency',
    ],
  },
  {
    title: 'Entry Setup',
    bullets: [
      'EMA gate must be confirmed before any orders are placed',
      'Initial Entry: limit order at 0.382 Fibonacci retracement',
      'Dip Buy 1: limit order at 0.17 Fibonacci retracement',
      'Both orders active simultaneously — shared stop at 0.000 (swing low/high)',
    ],
  },
  {
    title: 'Trade Management',
    bullets: [
      'Path A: full exit at 0.618 extension = +0.43R',
      'Path B: 50% off at 0.618, runner to 1.272 extension = +1.028R',
      'Path C (Dip Buy 1 filled): close at Initial Entry level = +0.38R',
      'Path D (full stop): contained risk — max loss is -1R',
    ],
  },
];

const smogContent: ModelSection[] = [
  {
    title: 'The Model',
    bullets: [
      'Smart Money OG — structural reversal trades using the OG Oscillator',
      'Trades against the prior trend when momentum exhaustion is confirmed',
      'Low win rate (~30%), but outsized winners via mandatory Partial Lock system',
    ],
  },
  {
    title: 'The Six Criteria',
    bullets: [
      '1. ADX below 25 on BOTH 1-min and 5-min timeframes (low momentum confirmed)',
      '2. OG Signal fires on BOTH price panel and oscillator panel',
      '3. ChoCh (structural break without FVG at level) OR CISD (structural break with FVG at level)',
      '4. FVG present — a 3-candle imbalance gap',
      '5. FVG sits at 50%+ Fibonacci retracement of the prior swing',
      '6. FVG confirmed — price returns to the gap and reacts',
    ],
  },
  {
    title: 'Partial Lock System',
    bullets: [
      'At 2R: close 50% of position — locks in +1.0R guaranteed floor',
      'Move remaining 50% stop to breakeven immediately',
      'Trail the runner using Heikin Ashi 15-min candle method',
      'Mandatory for ALL tiers, ALL setups — never optional',
    ],
  },
];

function VideoExplainer({ videoUrl, label }: { videoUrl?: string; label: string }) {
  if (videoUrl) {
    return (
      <div className="mb-6 rounded-xl overflow-hidden border border-white/[0.08]">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={videoUrl}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`${label} video explainer`}
          />
        </div>
      </div>
    );
  }

  // Placeholder — swap out when video is ready
  return (
    <div className="mb-6 rounded-xl overflow-hidden border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm">
      <div className="relative w-full flex flex-col items-center justify-center" style={{ paddingBottom: '56.25%' }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="w-16 h-16 rounded-full bg-white/[0.06] border border-white/[0.1] flex items-center justify-center">
            <Play className="w-7 h-7 text-white/30 ml-1" />
          </div>
          <span className="text-[11px] font-mono text-white/25 tracking-wider uppercase">{label} Video Explainer</span>
          <span className="text-[10px] font-mono text-white/15">Coming soon</span>
        </div>
      </div>
    </div>
  );
}

function ModelTab({ title, badge, sections, videoUrl }: { title: string; badge: { label: string; color: string }; sections: ModelSection[]; videoUrl?: string }) {
  const [expanded, setExpanded] = useState<number | null>(0);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold font-mono tracking-wide text-white/90">{title}</h2>
        <Badge label={badge.label} color={badge.color} />
      </div>

      <VideoExplainer videoUrl={videoUrl} label={badge.label} />

      <div className="flex flex-col gap-2">
        {sections.map((section, i) => (
          <Card key={i} className="!p-0">
            <button
              className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
              onClick={() => setExpanded(expanded === i ? null : i)}
            >
              <span className="text-base font-semibold font-mono">{section.title}</span>
              {expanded === i ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
            </button>
            {expanded === i && (
              <div className="px-4 pb-4">
                <ul className="flex flex-col gap-2">
                  {section.bullets.map((b, j) => (
                    <li key={j} className="text-sm font-mono text-white/35 flex gap-2 leading-relaxed">
                      <span className="text-white/20 shrink-0">*</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ModelExplainer() {
  const forgeNavigate = useForgeNavigate();
  const [activeTab, setActiveTab] = useState<'tcl' | 'smog'>('tcl');

  return (
    <PageWrapper warmth={0.1}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={3} />

        <div className="max-w-[660px] mx-auto px-8 pb-16">
          <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-shimmer text-center mb-3">Learn the Models</h1>
          <p className="text-[13px] font-mono text-white/35 text-center mb-8 tracking-wide">
            Understand both strategies before choosing your path.
          </p>

          {/* Tab switcher */}
          <div className="flex gap-2 mb-6 p-1 bg-white/[0.02] border border-white/[0.05] rounded-lg">
            <button
              className={`flex-1 py-2 text-[11px] font-semibold font-mono tracking-wider uppercase rounded-md transition-all duration-300 cursor-pointer ${
                activeTab === 'tcl' ? 'bg-white/[0.08] text-white border border-white/[0.12] shadow-[0_0_10px_rgba(255,255,255,0.03)]' : 'text-white/35 hover:text-white/60 border border-transparent'
              }`}
              onClick={() => setActiveTab('tcl')}
            >
              TCL Model
            </button>
            <button
              className={`flex-1 py-2 text-[11px] font-semibold font-mono tracking-wider uppercase rounded-md transition-all duration-300 cursor-pointer ${
                activeTab === 'smog' ? 'bg-white/[0.08] text-white border border-white/[0.12] shadow-[0_0_10px_rgba(255,255,255,0.03)]' : 'text-white/35 hover:text-white/60 border border-transparent'
              }`}
              onClick={() => setActiveTab('smog')}
            >
              SMOG Model
            </button>
          </div>

          {activeTab === 'tcl' && (
            <ModelTab title="TCL — Trend Continuation Ladder" badge={{ label: 'TCL', color: '#4A9EFF' }} sections={tclContent} />
          )}
          {activeTab === 'smog' && (
            <ModelTab title="SMOG — Smart Money Only Gains" badge={{ label: 'SMOG', color: '#3DBF7A' }} sections={smogContent} />
          )}

          <div className="mt-8 flex justify-center">
            <Button onClick={() => forgeNavigate('/resources', 3)}>
              Claim Your Toolkit
            </Button>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}
